import type { GrantedLevels } from '~/api/companyUsers';
import {
  ACCESS_LEVEL_LABEL,
  ACCESS_LEVEL_ORDER,
  type AccessLevel,
  FISCAL_MODULES,
  type FiscalModuleKey,
  MODULE_LABEL,
  atLeast,
} from '~/session/access';

export const GRANTING_CEILING_NOTE = 'Você só concede o que tem';

interface AccessLevelsPickerProps {
  value: GrantedLevels;
  /** O que o concedente tem na empresa: nada acima disso é oferecido. */
  ceiling: Record<FiscalModuleKey, AccessLevel>;
  onChange(module: FiscalModuleKey, level: AccessLevel): void;
  /** Recusa do backend por módulo (`access.nfse`), quando ela chega. */
  errors?: Partial<Record<FiscalModuleKey, string>>;
  disabled?: boolean;
}

/**
 * Um nível por módulo fiscal, como o convite e a edição do vínculo os concedem. Quem nega de
 * verdade é o backend (`assert_level_within_ceiling`, com o vínculo do concedente **nesta**
 * empresa); aqui as opções acima do próprio nível nem ficam clicáveis.
 */
export function AccessLevelsPicker({
  value,
  ceiling,
  onChange,
  errors = {},
  disabled = false,
}: AccessLevelsPickerProps): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {FISCAL_MODULES.map((module) => (
        <fieldset key={module} className="m-0 border-0 p-0">
          <legend className="p-0 font-mono text-[11px] uppercase tracking-overline text-ink-muted">
            {MODULE_LABEL[module]}
          </legend>
          <div className="mt-2 flex gap-1.5">
            {ACCESS_LEVEL_ORDER.map((level) => {
              const isSelected = value[module] === level;
              return (
                <button
                  key={level}
                  type="button"
                  aria-label={`${MODULE_LABEL[module]}: ${ACCESS_LEVEL_LABEL[level]}`}
                  aria-pressed={isSelected}
                  disabled={disabled || !atLeast(ceiling[module], level)}
                  onClick={() => onChange(module, level)}
                  className={`${isSelected ? 'rx-chip rx-chip-active' : 'rx-chip'} disabled:opacity-40`}
                >
                  {ACCESS_LEVEL_LABEL[level]}
                </button>
              );
            })}
          </div>
          {errors[module] && (
            <p role="alert" className="mt-1 text-[12px] text-negative">
              {errors[module]}
            </p>
          )}
        </fieldset>
      ))}
      <p className="text-[12px] text-ink-subtle">{GRANTING_CEILING_NOTE}</p>
    </div>
  );
}
